/**
 * Global event bus for **`SearchSuggest`**.
 *
 * Mount a single **`SearchSuggest`** instance somewhere stable (typically the
 * design-system page or app shell) and subscribe to this bus inside a
 * **`useEffect`**. Any opener in any subtree (template-level nav, header
 * **`SearchToggle`**, in-page buttons) can then call **`searchSuggestBus.open()`**
 * without having to thread a ref through the React tree.
 *
 * Example consumer:
 *
 * ```tsx
 * const ref = useRef<SearchSuggestHandle>(null);
 * useEffect(() => searchSuggestBus.subscribe((action) => {
 *   if (action === 'open') ref.current?.openSearch();
 *   if (action === 'close') ref.current?.closeSearch();
 *   if (action === 'toggle') ref.current?.toggleSearch();
 * }), []);
 * ```
 */
export type SearchSuggestBusAction = 'open' | 'close' | 'toggle';
export type SearchSuggestBusHandler = (action: SearchSuggestBusAction) => void;

const handlers = new Set<SearchSuggestBusHandler>();

const dispatch = (action: SearchSuggestBusAction) => {
  handlers.forEach((handler) => {
    try {
      handler(action);
    } catch {
      // Swallow handler errors — one broken subscriber must not block others.
    }
  });
};

export const searchSuggestBus = {
  subscribe(handler: SearchSuggestBusHandler): () => void {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  },
  open(): void {
    dispatch('open');
  },
  close(): void {
    dispatch('close');
  },
  toggle(): void {
    dispatch('toggle');
  },
};

export default searchSuggestBus;
